import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SEO from '../components/common/SEO';
import Breadcrumb from '../components/common/Breadcrumb';
import SafeIcon from '../components/common/SafeIcon';

const { FiDatabase, FiSearch, FiBarChart3, FiUsers, FiShield, FiZap } = FiIcons;

function AboutPage() {
  const breadcrumbItems = [
    { name: 'About' }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is Pokemon Stats Database?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Pokemon Stats Database is a comprehensive online database containing detailed information about 1,215+ Pokemon, including their base stats, abilities, types, and other essential data."
        }
      },
      {
        "@type": "Question", 
        "name": "How accurate is the Pokemon data?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our data is sourced from official Pokemon games and regularly updated to ensure accuracy. All stats, abilities, and Pokemon information reflect the latest game data."
        }
      },
      {
        "@type": "Question",
        "name": "Can I compare Pokemon stats?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! Our comparison tool allows you to compare up to 4 Pokemon side-by-side, making it easy to analyze their strengths and weaknesses."
        }
      }
    ]
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-50 py-8"
    >
      <SEO
        title="About Pokemon Stats Database - Complete Pokemon Reference"
        description="Learn about Pokemon Stats Database, the most comprehensive Pokemon reference with 1,215+ Pokemon, detailed stats, abilities, and comparison tools."
        keywords="about pokemon database, pokemon stats, pokemon reference, pokemon information"
        url="/about"
        schema={faqSchema}
      />

      <div className="container mx-auto px-4">
        <Breadcrumb items={breadcrumbItems} />

        {/* Header */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">About Pokemon Stats Database</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            The most comprehensive Pokemon reference database with detailed statistics, abilities, and comparison tools for trainers worldwide.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Main Content */}
          <div className="space-y-8">
            {/* Our Mission */}
            <motion.section
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-lg shadow-lg p-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                <SafeIcon icon={FiZap} className="w-6 h-6 text-blue-600" />
                <span>Our Mission</span>
              </h2>
              <p className="text-gray-600 mb-4">
                Pokemon Stats Database was created to provide trainers, competitive players, and Pokemon enthusiasts with the most accurate and comprehensive Pokemon data available. We believe that having access to detailed statistics and information enhances the Pokemon experience for everyone.
              </p>
              <p className="text-gray-600">
                Our goal is to be the go-to resource for Pokemon information, offering not just basic stats but also advanced features like Pokemon comparison, type effectiveness, and breeding information.
              </p>
            </motion.section>

            {/* What We Offer */}
            <motion.section
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-lg shadow-lg p-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">What We Offer</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiDatabase} className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Complete Database</h3>
                    <p className="text-gray-600 text-sm">1,215+ Pokemon with detailed stats, abilities, and information</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiSearch} className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Advanced Search</h3>
                    <p className="text-gray-600 text-sm">Find Pokemon by name, type, abilities, or stat ranges</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiBarChart3} className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Comparison Tools</h3>
                    <p className="text-gray-600 text-sm">Compare up to 4 Pokemon side-by-side with detailed analysis</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiUsers} className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Community Focus</h3>
                    <p className="text-gray-600 text-sm">Built by trainers, for trainers with regular updates</p>
                  </div>
                </div>
              </div>
            </motion.section>

            {/* Data Sources */}
            <motion.section
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-lg shadow-lg p-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                <SafeIcon icon={FiShield} className="w-6 h-6 text-green-600" />
                <span>Data Accuracy</span>
              </h2>
              <p className="text-gray-600 mb-4">
                All Pokemon data is sourced directly from official Pokemon games and cross-referenced with multiple reliable sources to ensure accuracy. We regularly update our database to reflect the latest game releases and balance changes.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Base stats from official Pokemon games</li>
                <li>Abilities and hidden abilities verified across generations</li>
                <li>Type effectiveness based on current game mechanics</li>
                <li>Breeding information from official sources</li>
                <li>Images sourced from official Pokemon artwork</li>
              </ul>
            </motion.section>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Key Features */}
            <motion.section
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-lg shadow-lg p-6"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-6">Key Features</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-bold text-sm">1K+</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Pokemon Database</div>
                    <div className="text-sm text-gray-600">Complete information for 1,215+ Pokemon</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 font-bold text-sm">18</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Pokemon Types</div>
                    <div className="text-sm text-gray-600">All 18 types with detailed pages</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-purple-600 font-bold text-sm">7</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Top Lists</div>
                    <div className="text-sm text-gray-600">Rankings by different stats</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                    <span className="text-yellow-600 font-bold text-sm">SEO</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">SEO Optimized</div>
                    <div className="text-sm text-gray-600">Fast loading and search engine friendly</div>
                  </div>
                </div>
              </div>
            </motion.section>

            {/* FAQ */}
            <motion.section
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-lg shadow-lg p-6"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h3>
              <div className="space-y-4">
                <details className="group">
                  <summary className="font-semibold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors">
                    How often is the data updated?
                  </summary>
                  <p className="mt-2 text-gray-600 text-sm">
                    We update our database regularly, especially when new Pokemon games are released or when balance changes are made to existing Pokemon.
                  </p>
                </details>
                
                <details className="group">
                  <summary className="font-semibold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors">
                    Can I use this data for my own projects?
                  </summary>
                  <p className="mt-2 text-gray-600 text-sm">
                    All Pokemon data is publicly available information. However, please respect copyright for images and artwork.
                  </p>
                </details>
                
                <details className="group">
                  <summary className="font-semibold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors">
                    Do you have an API?
                  </summary>
                  <p className="mt-2 text-gray-600 text-sm">
                    Currently, we don't offer a public API, but we're considering it for future development based on user demand.
                  </p>
                </details>
                
                <details className="group">
                  <summary className="font-semibold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors">
                    How can I report incorrect data?
                  </summary>
                  <p className="mt-2 text-gray-600 text-sm">
                    Please use our contact form to report any inaccuracies. We take data quality seriously and will investigate all reports.
                  </p>
                </details>
              </div>
            </motion.section>

            {/* Statistics */}
            <motion.section
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-6">By the Numbers</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">1,215+</div>
                  <div className="text-sm text-gray-600">Pokemon</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">18</div>
                  <div className="text-sm text-gray-600">Types</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">200+</div>
                  <div className="text-sm text-gray-600">Abilities</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">9</div>
                  <div className="text-sm text-gray-600">Generations</div>
                </div>
              </div>
            </motion.section>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default AboutPage;